import { type Direction, type Side } from "src/engine/rules/board";
import { styled } from "./kit";
import {
  type CSSProperties,
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from "react";

import { exposeForTests } from "src/engine/game/debugHook";
import { type BoardHighlights, BoardRenderer } from "src/client/ai/render/BoardRenderer";
import type { CameraPose, Projection, Tilt } from "src/client/ai/render/cameraRig";
import type { SoundListener } from "src/client/ai/render/soundEvents";
import { DIRECTION_REMOVE } from "src/client/ai/render/directionPicker";
import type { PiecePack } from "src/client/ai/render/models";
import type { Tool } from "./tool";
import type { Game, Square } from "src/api";
import { DIR_NAME } from "src/client/ai/render/factNames";
import { useCarryGesture } from "./useCarryGesture";

export interface BoardCanvasHandle {
  rotate(steps?: number): void;
  /** Back to the default angle and zoom, keeping the current facing. */
  resetView(): void;
  /** Orthographic or perspective. See `CameraRig`. */
  setProjection(mode: Projection): void;
  /** Pull back until every square is on screen. */
  fitView(): void;
  /** Look across the board or down onto it. See `CameraRig.setTilt`. */
  setTilt(tilt: Tilt): void;
  /**
   * Point the held pan keys: a screen-relative direction, each part −1, 0 or 1.
   *
   * A direction rather than a step, so the rig owns the speed and a held key
   * glides at one rate instead of at the platform's repeat rate.
   * `CameraRig.drive`.
   */
  driveCamera(right: number, forward: number): void;
  /**
   * Take the view to these squares, and *keep* it there.
   *
   * `lookAt`'s twin, and the difference is whose the look is: this one moves
   * the anchor because the player asked for it, so nothing hands the board
   * back a moment later.
   */
  jumpTo(squares: readonly number[]): void;
  pulse(square: number): void;
  /** Swap the piece models. Developer-only; see `PIECE_PACKS`. */
  setPiecePack(pack: PiecePack, side?: number | "default"): void;
  focusSide(side: Side): void;
  centreCamera(): void;
  /** Viewport position of a square's centre, for anchoring DOM overlays. */
  projectSquare(square: number): { x: number; y: number };
  /**
   * The square's actual centre, rather than the corner badges hang off.
   *
   * Labels name the square they sit on, so they want its middle — a corner
   * would put every one of them a half-square off toward the board.
   */
  projectCentre(square: number): { x: number; y: number };
  /** Run after each rendered frame, so overlays anchor to the drawn camera. */
  onFrame(listener: () => void): () => void;
  /**
   * Hear what the board does — a piece setting off, an oven lighting, ground
   * arriving. Returns an unsubscribe.
   *
   * Visual moments, never sounds: what to play is decided a layer up, so the
   * scene knows nothing about the audio pack. See `render/soundEvents.ts`.
   */
  onSound(listener: SoundListener): () => void;
  /**
   * Where the camera is, for anything placing sound in the board's own space.
   *
   * Null before there is a renderer, which is the honest answer — a caller
   * cannot position anything against a camera that does not exist yet.
   */
  cameraPose(): CameraPose | null;
  /**
   * Where the board's middle is on screen, so the interface can take the other
   * side. Null before the first frame, or when there is no board yet.
   */
  boardCentre(): { x: number; y: number } | null;
  /** How much of the canvas the interface covers, so the board centres in the rest. */
  setViewInset(inset: { left: number; right: number; top: number; bottom: number }): void;
  /** Which way the board is facing on screen, in radians. */
  viewYaw(): number;
  /** Move the view to the middle of these squares, easing there. */
  /** `fit` pulls back to take in the whole board — for new ground. */
  lookAt(squares: readonly number[], fit?: boolean): void;
  /**
   * Are these squares already comfortably on screen — inside the inner eight
   * tenths of the viewport?
   *
   * What a per-move look asks before it takes the view. True with no renderer,
   * which is the safe answer: nothing to move the board *with*, and nothing to
   * move it *for*.
   */
  isOnScreen(squares: readonly number[]): boolean;
  /** Hand the view back to wherever the player last left it. */
  returnView(): void;
  /** True when no piece, tile or camera movement is still in flight. */
  isSettled(): boolean;
  /**
   * How much animation time the board is getting per second of wall clock.
   *
   * 1 while it is keeping up, and 1 before there is a renderer at all. See
   * `BoardRenderer.clockHealth` — the driver paces its commands off it so it
   * cannot deal them faster than the board can draw what they mean.
   */
  clockHealth(): number;
}

interface Props {
  /**
   * The game to draw. The renderer subscribes to it and is handed every
   * change as a `TurnDelta` beside the viewed board's facts — which step of
   * history is on screen, what the change tint is and whether a move was
   * played are all the engine's answers now (PLAN-CLIENT-SPLIT §2.2).
   */
  game: Game;
  /**
   * Whether a gesture reaches the game. **The canvas drives `game.session`
   * directly** (PLAN-CLIENT-SPLIT day 8): a click is `tap`, a drag with the
   * Build tool `beginPath`/`extendPath`/`endPath`, a
   * lifted piece `carry`/`drop`, and what is lit is `view.highlights`. False
   * for a board nobody may act on — a watched game, a report, the tuner's
   * frame — where every gesture is the camera's and nothing else.
   */
  interactive?: boolean;
  /** The pointer entered or left the board, for chrome that shows on hover. */
  onHoverChange?(over: boolean): void;
  /**
   * The piece set every army wears unless dressed individually.
   *
   * A prop rather than a call on the handle, because the renderer is built in
   * an effect here: a caller applying a pack from its own mount is talking to
   * something that does not exist yet, and the call goes nowhere.
   */
  pack?: PiecePack;
  /**
   * Orthographic or perspective. See `CameraRig`.
   *
   * A prop for exactly the reason `pack` is one, and it is the same trap: the
   * renderer is built in an effect here, so a caller pushing a stored setting
   * from its own mount is talking to something that does not exist yet and the
   * call is dropped in silence. As a prop the effect below re-runs when the
   * renderer appears.
   */
  projection?: Projection;
  /**
   * Across the table or straight down. See `CameraRig.setTilt`.
   *
   * A prop for exactly the reason `projection` is one, and it is the same
   * trap: the renderer is built in an effect here, so a stored setting pushed
   * from a caller's own mount reaches nothing. Pitch is a preference now
   * rather than the second axis of a drag.
   */
  tilt?: Tilt;
  /**
   * May a move's reaction borrow the view — "Move the camera for me" and
   * "Follow the piece that moved", both on. See `BoardRenderer.setFollowMoves`.
   * On unless told, which is the behaviour that shipped.
   */
  followMoves?: boolean;
  /**
   * Play an opponent's factory as its own beat behind the move, or with it.
   *
   * A prop for the same reason `projection` is one, and it defaults to the
   * staged reading — so a caller that says nothing gets the behaviour that
   * shipped. See `BoardRenderer.setStaging`.
   */
  staging?: boolean;
  /**
   * Multiply playback tempo — a transport's speed times the player's own
   * animation scale. See `GameShell`'s `playbackPace`.
   */
  paceScale?: number;
  /**
   * Cap how often the board draws. `0` or undefined is off.
   *
   * Separate from `paceScale`, and they are different complaints: that one is
   * how fast the board plays, this is how many pictures of it get drawn. See
   * `render/frameCap.ts`.
   */
  maxFps?: number;
  /**
   * What a drag does. It used to be inferred from what was under the pointer,
   * which left no way to turn the board while standing over your own ground.
   */
  tool?: Tool;
  ref?: Ref<BoardCanvasHandle>;
}

/**
 * Owns the renderer's lifetime and forwards DOM events into it. Deliberately
 * the only file that touches both React and src/client/ai/render.
 */
/** Total pointer travel, in pixels, that turns a click into a camera drag. */
const DRAG_THRESHOLD_PX = 6;

/** One notch of a mouse wheel, in the pixels browsers report for it. */
const NOTCH_PX = 100;
/**
 * A trackpad pinch arrives as a wheel event with ctrl held — the browser's own
 * convention, not a guess — and its deltas are an order of magnitude smaller
 * than a wheel's. Without this a pinch moves the view so little it reads as
 * the gesture not being supported.
 */
const PINCH_GAIN = 4;

/**
 * A wheel event as notches, whatever the device reported it in.
 *
 * `deltaMode` is the trap: Firefox reports lines and a page-mode device
 * reports screens, so reading `deltaY` raw makes the wheel a hundred times
 * less sensitive on one browser than on another — and the usual response is to
 * tune the constant against whichever browser it was tested in.
 */
function wheelNotches(event: WheelEvent): number {
  const perUnit =
    event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
  const pixels = event.deltaY * perUnit * (event.ctrlKey ? PINCH_GAIN : 1);
  return pixels / NOTCH_PX;
}

/**
 * True for the tools whose subject is the floor rather than what is on it.
 *
 * Two things follow from it and they are the same idea twice: the raycast
 * looks straight past the pieces, and the pieces are drawn back so you can see
 * what you are aiming at. A lane may run *under* an army now, so the square
 * being clicked is routinely behind the piece that used to swallow the click.
 */
const aboutTheFloor = (tool: Tool): boolean => tool === "build" || tool === "erase";

/**
 * The canvas itself, and the whole of this file's styling.
 *
 * **The cursor travels as a custom property**, not as a styled prop: it
 * changes on every pointer move, and a `styled` variant or an `sx` value mints
 * a permanent emotion class per distinct value. The `:active` rule needs a
 * selector, which `style` cannot carry — so the value goes out in `style` and
 * the rule reads it, and the class is minted once.
 */
const Surface = styled("canvas")({
  width: "100%",
  height: "100%",
  display: "block",
  touchAction: "none",
  cursor: "var(--cursor)",
  "&:active": { cursor: "var(--cursor-down)" }
});

/**
 * The cursor for whatever is under the pointer.
 *
 * Ordered the way the click handler resolves: the picker's arrows overhang
 * neighbouring tiles and win, then anything on a square, then bare board —
 * so the cursor cannot promise something the click will not deliver.
 */
function cursorFor(
  renderer: BoardRenderer,
  x: number,
  y: number,
  tool: Tool,
  canCarry?: (square: number) => boolean,
): string {
  // The tool speaks first: with one selected, the drag it describes is what
  // the pointer is for, and a cursor promising a click on a piece would be
  // describing the wrong gesture.
  if (tool === "erase") return "not-allowed";
  // Drawing, whatever is standing on the square — the piece is not what this
  // gesture is about, so a "click me" pointer over one would misdescribe it.
  if (tool === "build") return "crosshair";
  if (renderer.pickDirection(x, y) >= 0) return "pointer";
  const square = renderer.pick(x, y);
  if (square < 0) return "grab";
  // The selected piece can be picked up, and `grab` turns to `grabbing` on the
  // press — the one cursor pair that already says so.
  if (canCarry?.(square)) return "grab";
  return renderer.hasSomethingAt(square) ? "pointer" : "cell";
}

/** Nothing lit, as one identity, so a still board never resubscribes the renderer. */
const NO_HIGHLIGHTS: BoardHighlights = {};

/** A still board's session: every gesture lands on nothing. */
const NO_SESSION = {
  subscribe: () => () => {},
  revision: () => 0
};

export function BoardCanvas({
  game,
  interactive = true,
  onHoverChange,
  tool = "turn",
  pack = "base",
  projection = "orthographic",
  tilt = "table",
  followMoves = true,
  staging = true,
  paceScale = 1,
  maxFps = 0,
  ref,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * **The session is the gesture's other end.** Every pointer event below
   * resolves to one of its verbs, and what the renderer lights is its view.
   * A still board (no session) or a board nobody may act on (`interactive`
   * false) gets the no-op set, so the handlers below need no second branch.
   */
  const session = interactive ? game.session : undefined;
  const revision = useSyncExternalStore(
    session ? session.subscribe : NO_SESSION.subscribe,
    session ? () => session.revision : NO_SESSION.revision,
    session ? () => session.revision : NO_SESSION.revision
  );
  const view = useMemo(() => session?.view(), [session, revision]);
  const highlights: BoardHighlights = view?.highlights ?? NO_HIGHLIGHTS;
  const offBoard = view?.offBoard ?? false;
  const carrying = view?.carrying ?? false;
  const carry = view?.carry;
  // The gesture names the handlers were written against, bound to the verbs.
  const onPickSquare = (square: number) => session?.tap(square as Square);
  const onPickNothing = () => session?.cancel();
  // Shift on a re-aim arrow also walks the pawn a square (§31 P1).
  const onPickDirection = (direction: Direction, step = false) =>
    session?.face(DIR_NAME[direction], step ? { step } : undefined);
  const onRemovePicked = () => session?.removePicked();
  const onDoubleClickSquare = (square: number) => session?.doubleTap(square as Square);
  const onStartPath = (square: number) => session?.beginPath(square as Square) ?? false;
  const onExtendPath = (square: number) => session?.extendPath(square as Square);
  const onCommitPath = () => session?.endPath();
  const onDiscard = (square: number, press: boolean) => session?.discard(square as Square, press);
  const onHoverSquare = (square: number) => session?.hover(square as Square | -1);
  const onCanCarry = (square: number) => session?.canCarry(square as Square) ?? false;
  const onBeginCarry = (square: number, at: { x: number; y: number }) =>
    session?.carry(square as Square, at);
  const onDropCarry = (square: number) => session?.drop(square as Square | -1);

  // Through a ref, so the renderer effect can read it without listing it as a
  // dependency — a pack change must not tear the renderer down and rebuild it.
  const packRef = useRef(pack);
  packRef.current = pack;
  const [renderer, setRenderer] = useState<BoardRenderer>();
  // **The button travels with the drag.** Only a left press can resolve into a
  // click on release; a middle or right one is a camera gesture from start to
  // finish, and a stationary middle click landing on a square was a piece
  // selected by a gesture that has nothing to do with pieces.
  const drag = useRef<{ x: number; y: number; distance: number; button: number } | null>(
    null,
  );
  /** True while a pointer-down is drawing a lane rather than moving the view. */
  const drawing = useRef(false);
  /**
   * True while a drag is moving the view across the board.
   *
   * **What a drag falls back to**, under every tool: the commonest thing
   * anybody does on a board this size is look somewhere else, and making that
   * modal is what made the camera feel like a tool bar.
   */
  const panning = useRef(false);
  /** True while a drag is swinging the board around — shift and a left button. */
  const turning = useRef(false);
  /** True while a pointer-down is sweeping parts off the board. */
  const erasing = useRef(false);
  /**
   * Every pointer currently down, so a second one can be noticed.
   *
   * The whole of the touch story: one finger is the gesture the mouse has, two
   * are a pinch. Kept as a map rather than a count because the pinch needs
   * both positions to measure the distance between them.
   */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  /** The last distance between the two pinching fingers, or null for no pinch. */
  const pinch = useRef<number | null>(null);
  /**
   * A second finger has landed, so no single-pointer gesture may start.
   *
   * Cleared only when every pointer is up: lifting one finger of a pinch
   * leaves the other one down, and reading that as the start of a pan sends
   * the board flying on the way to putting the hand down.
   */
  const multi = useRef(false);
  /**
   * A press on a piece that may be picked up, and whether it has been yet.
   *
   * Decided on the press like every other claim on the drag, begun past the
   * click threshold so a shaky click is still a click — and from there the
   * pointer is `useCarryGesture`'s, which is how a carry off the hotbar and
   * one off the board end in the same place.
   */
  const lifting = useRef<{ square: number; begun: boolean } | null>(null);
  /**
   * What the pointer is over, as a CSS cursor.
   *
   * There is a lot on a square — a piece, a part, a heap, an arrow of the
   * picker, ground you may build on — and until now every one of them looked
   * identical to the pointer. The cursor is the cheapest possible answer to
   * "is there anything here", and it costs a raycast that is already happening.
   */
  const [cursor, setCursor] = useState("grab");
  /** Last square reported to `onHoverSquare`, so only changes are sent up. */
  const hovered = useRef(-1);

  // Later changes — the dev menu switching sets while a game is up. Creation
  // is handled inside the renderer effect below, because at that moment there
  // is no renderer for this to talk to.
  useEffect(() => {
    if (renderer) void renderer.setPiecePack(pack);
  }, [renderer, pack]);

  // **`renderer` in the list, not just the setting.** That is what covers the
  // mount: the state lands after the renderer effect runs, so this fires again
  // the moment there is something to tell — the same reason `pack` lists it.
  // No creation-time twin needed, unlike the pack, because the rig starts
  // orthographic and this is a cheap call on a settled camera.
  useEffect(() => {
    renderer?.setProjection(projection);
  }, [renderer, projection]);

  // `renderer` in the list for the reason `projection` lists it: the setting is
  // read from storage on the caller's mount, which is before this exists.
  useEffect(() => {
    renderer?.setTilt(tilt);
  }, [renderer, tilt]);

  // `renderer` in the list for the reason above.
  useEffect(() => {
    renderer?.setFollowMoves(followMoves);
  }, [renderer, followMoves]);

  // `renderer` in the list for the reason above: the setting is read from
  // storage on the caller's mount, which is before this renderer exists.
  useEffect(() => {
    renderer?.setStaging(staging);
  }, [renderer, staging]);

  useEffect(() => {
    renderer?.setPaceScale(paceScale);
  }, [renderer, paceScale]);

  // `renderer` in the list for the reason the three above list it: the setting
  // arrives with the launch, which is before this renderer exists.
  useEffect(() => {
    renderer?.setMaxFps(maxFps);
  }, [renderer, maxFps]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const instance = new BoardRenderer(canvas);
    // **The pack, before anything is drawn.** The renderer is created in *this*
    // effect, so a caller applying a pack on its own mount is talking to a
    // renderer that does not exist yet and the call is silently dropped —
    // `?pack=` booted the base set and only took effect when somebody opened
    // the dev menu. Same trap as the frame listeners above it.
    if (packRef.current !== "base") void instance.setPiecePack(packRef.current);
    setRenderer(instance);
    exposeForTests({ renderer: instance });

    const observer = new ResizeObserver(() => instance.resize());
    observer.observe(canvas);

    return () => {
      observer.disconnect();
      instance.dispose();
      setRenderer(undefined);
    };
  }, []);

  /**
   * The wheel, attached by hand rather than as `onWheel`.
   *
   * React registers its wheel listener on the root as **passive**, so
   * `preventDefault` inside a JSX handler silently does nothing — and what it
   * needs to prevent is real: ctrl-wheel is the browser's own page zoom, which
   * on a trackpad pinch would scale the entire UI instead of the board.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !renderer) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const notches = wheelNotches(event);
      if (notches !== 0) renderer.zoomAt(event.clientX, event.clientY, notches);
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [renderer]);

  /**
   * Frame subscribers, held here rather than on the renderer.
   *
   * The renderer is created in an effect, so the imperative handle exists for
   * a moment with nothing behind it. Subscribing straight through dropped any
   * listener that registered in that window — and since the board is settled
   * at that point it renders no frames, so nothing ever came along to retry.
   * The overlay silently never appeared.
   */
  const frameListeners = useRef(new Set<() => void>());
  /**
   * Sound subscribers, held here for exactly the reason above.
   *
   * The trap is sharper for these than for frames: audio is set up on the
   * shell's mount, which is before this renderer exists, and the board draws
   * frames whether or not anyone is listening — so a listener dropped in that
   * window would never be retried and the game would simply be silent.
   */
  const soundListeners = useRef(new Set<SoundListener>());
  /** What the interface is covering. See the handle's `setViewInset`. */
  const viewInset = useRef({ left: 0, right: 0, top: 0, bottom: 0 });

  // Subscribed the moment the renderer exists, and unsubscribed with it: the
  // set outlives the renderer, the subscription does not.
  useEffect(() => {
    if (!renderer) return;
    return renderer.onSound((event) => {
      for (const listener of soundListeners.current) listener(event);
    });
  }, [renderer]);

  // Applied the moment the renderer exists, whenever that turns out to be.
  useEffect(() => {
    renderer?.setViewInset(viewInset.current);
  }, [renderer]);

  useEffect(() => {
    if (!renderer) return;
    return renderer.onFrame(() => {
      for (const listener of frameListeners.current) listener();
    });
  }, [renderer]);

  // **Sometimes a click has to be able to land off the board.** `pick` refuses
  // anything that is not a cell, which is right for every other gesture and
  // exactly wrong for placing a Bridges tile, whose target is by definition not
  // board yet. Pushed like the pack rather than passed to `pick`, because the
  // pointer handlers know about screens and not about runs.
  //
  // The Spatial Manifold used to want this too, for its four off-board edge
  // marks. It pushes every edge at once now and takes no target, so Bridges is
  // the only gesture left that reaches past the frontier.
  useEffect(() => renderer?.setOffBoard(offBoard ?? false), [renderer, offBoard]);
  useEffect(() => renderer?.setGame(game), [renderer, game]);
  useEffect(() => renderer?.setHighlights(highlights), [renderer, highlights]);
  useCarryGesture(renderer, canvasRef, carrying, carry, (square) => onDropCarry?.(square));
  // The army steps back while a belt tool is up, because the squares the tool
  // is about are the ones the army is standing on.
  useEffect(
    () => renderer?.setPiecesFaded(aboutTheFloor(tool)),
    [renderer, tool],
  );

  useImperativeHandle(
    ref,
    () => ({
      rotate: (steps = 1) => renderer?.rotate(steps),
      resetView: () => renderer?.resetView(),
      setProjection: (mode: Projection) => renderer?.setProjection(mode),
      fitView: () => renderer?.fitView(),
      setTilt: (tilt: Tilt) => renderer?.setTilt(tilt),
      driveCamera: (right: number, forward: number) =>
        renderer?.driveCamera(right, forward),
      jumpTo: (squares: readonly number[]) =>
        renderer?.lookAtSquares(squares, false, true),
      pulse: (square: number) => renderer?.pulse(square),
      setPiecePack: (pack: PiecePack, side: number | "default" = "default") =>
        void renderer?.setPiecePack(pack, side as never),
      focusSide: (side: Side) => renderer?.focusSide(side),
      centreCamera: () => renderer?.centreCamera(),
      projectSquare: (square: number) =>
        renderer?.projectSquareCorner(square) ?? { x: -1000, y: -1000 },
      projectCentre: (square: number) =>
        renderer?.projectSquare(square) ?? { x: -1000, y: -1000 },
      lookAt: (squares: readonly number[], fit?: boolean) =>
        renderer?.lookAtSquares(squares, fit),
      isOnScreen: (squares: readonly number[]) => renderer?.onScreen(squares) ?? true,
      returnView: () => renderer?.returnView(),
      isSettled: () => renderer?.boardSettled ?? true,
      clockHealth: () => renderer?.clockHealth ?? 1,
      onFrame: (listener: () => void) => {
        frameListeners.current.add(listener);
        return () => frameListeners.current.delete(listener);
      },
      onSound: (listener: SoundListener) => {
        soundListeners.current.add(listener);
        return () => {
          soundListeners.current.delete(listener);
        };
      },
      cameraPose: () => renderer?.cameraPose() ?? null,
      boardCentre: () => renderer?.boardScreenCentre() ?? null,
      viewYaw: () => renderer?.viewYaw ?? 0,
      setViewInset: (next) => {
        // **Remembered, not merely forwarded.** The renderer is created in an
        // effect, so the handle exists a commit before it does — and the
        // shell sets the inset on *its* mount, which is earlier still. Sent
        // straight through, that first call reached `renderer?.` while it was
        // undefined and vanished, leaving the board centred in the window
        // with its last two files behind the panel and nothing to re-trigger
        // it. Exactly the trap `onFrame` above is written the way it is to
        // avoid.
        viewInset.current = next;
        renderer?.setViewInset(next);
      },
    }),
    [renderer],
  );

  /**
   * Where the two pinching fingers are: how far apart, and their middle.
   *
   * The first two down, whatever else lands after them — a third finger is not
   * a third meaning, it is a hand resting on a tablet.
   */
  const pinchSpan = (): { distance: number; x: number; y: number } | null => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return null;
    return {
      distance: Math.hypot(b.x - a.x, b.y - a.y),
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2
    };
  };

  /**
   * Put down whatever one pointer was doing.
   *
   * `settle` is the difference between a pointer lifting and a pinch taking
   * over: a lifted gesture ends throw and all, while a gesture a second finger
   * interrupted must not throw the view — so the coast the pan recorded is
   * dropped and the drag settles without moving anything.
   */
  const endGesture = (instance: BoardRenderer, settle: boolean) => {
    if (settle) instance.endCameraDrag();
    else instance.cancelCameraDrag();
    drag.current = null;
    drawing.current = false;
    erasing.current = false;
    panning.current = false;
    turning.current = false;
    lifting.current = null;
    onCommitPath();
  };

  return (
    <Surface
      ref={canvasRef}
      data-testid="board-canvas"
      style={
        {
          "--cursor": carrying ? "grabbing" : cursor,
          "--cursor-down": carrying || cursor === "grab" ? "grabbing" : cursor
        } as CSSProperties
      }
      onPointerDown={(event) => {
        if (!renderer) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

        // **A second finger takes the gesture off whatever had it.** The hand
        // is pinching, and the first finger's few pixels of travel on the way
        // to the second one are not a pan.
        if (pointers.current.size >= 2) {
          if (!multi.current) {
            multi.current = true;
            endGesture(renderer, false);
          }
          pinch.current = pinchSpan()?.distance ?? null;
          return;
        }
        // Fingers still coming off a pinch: nothing new starts until the hand
        // is off the glass.
        if (multi.current) return;

        drag.current = {
          x: event.clientX,
          y: event.clientY,
          distance: 0,
          button: event.button
        };

        const square = renderer.pick(
          event.clientX,
          event.clientY,
          aboutTheFloor(tool),
        );

        // **The right and middle buttons always pan, whatever the tool says.**
        // Neither has another meaning on a board — there is no right-click
        // action — and both are what people reach for on a map. Having to
        // switch tools to nudge the view while building a lane is the kind of
        // friction that makes a tool bar feel like a mode.
        const cameraButton = event.button === 1 || event.button === 2;
        // **Shift turns; everything else that is left over pans.** Turning is
        // the rarer half by a long way and it is the one that leaves the board
        // askew, so it is the one that has to be asked for.
        turning.current = !cameraButton && event.shiftKey;

        erasing.current = !cameraButton && !turning.current && tool === "erase";
        if (erasing.current) {
          if (square >= 0) onDiscard(square, true);
          return;
        }

        // Build only arms on ground you may actually lay on. Elsewhere the
        // drag falls back to moving the view rather than doing nothing — being
        // stuck with a dead pointer is worse than a camera you did not quite
        // ask to move.
        drawing.current =
          !turning.current && tool === "build" && square >= 0 && onStartPath(square);

        // The selected piece, picked up. Not under a floor tool, whose drags
        // are about the square and look straight past what stands on it.
        lifting.current =
          !cameraButton &&
          !turning.current &&
          !aboutTheFloor(tool) &&
          square >= 0 &&
          onCanCarry?.(square)
            ? { square, begun: false }
            : null;

        // Whatever nothing above claimed. The commonest thing anybody does on
        // a board this size is look somewhere else, so that is what a drag
        // falls back to under every tool.
        panning.current =
          !turning.current &&
          !erasing.current &&
          !drawing.current &&
          !lifting.current;
      }}
      onPointerMove={(event) => {
        if (!renderer) return;
        if (pointers.current.has(event.pointerId)) {
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        }

        // **The pinch owns the move while two fingers are down.** Zoom toward
        // the middle of the two, for the reason the wheel zooms toward the
        // pointer: what you put your fingers on is what you are looking at.
        if (multi.current) {
          const span = pinchSpan();
          if (!span) return;
          // **The ratio is inverted, because the factor is a view *height*:**
          // fingers spreading means the board is bigger and the view smaller.
          if (span.distance > 0 && pinch.current) {
            renderer.zoomBy(span.x, span.y, pinch.current / span.distance);
          }
          pinch.current = span.distance;
          return;
        }

        const current = drag.current;

        // Hover: say what a click would mean here. Only while not dragging —
        // mid-drag the gesture has already committed to something.
        if (!current) {
          setCursor(
            cursorFor(renderer, event.clientX, event.clientY, tool, onCanCarry),
          );
          // The same raycast the cursor already needed, reported up only when
          // the answer changes — a half-drawn run previews itself at whatever
          // square the pointer is over, and has no other way to find out.
          const over = renderer.pick(
            event.clientX,
            event.clientY,
            aboutTheFloor(tool),
          );
          if (over !== hovered.current) {
            hovered.current = over;
            onHoverSquare?.(over);
          }
          return;
        }

        const dx = event.clientX - current.x;
        const dy = event.clientY - current.y;
        current.x = event.clientX;
        current.y = event.clientY;
        current.distance += Math.abs(dx) + Math.abs(dy);

        // Both sweeps look past the pieces: a lane can run under one, so a
        // piece standing in the way of the pointer is not standing in the way
        // of the gesture.
        if (erasing.current) {
          const square = renderer.pick(event.clientX, event.clientY, true);
          if (square >= 0) onDiscard(square, false);
          return;
        }

        if (drawing.current) {
          const square = renderer.pick(event.clientX, event.clientY, true);
          if (square >= 0) onExtendPath(square);
          return;
        }

        // Only start moving the view once the pointer has clearly travelled,
        // so a slightly shaky click still selects a piece instead of nudging
        // the camera and eating the move.
        if (current.distance <= DRAG_THRESHOLD_PX) return;
        if (lifting.current) {
          if (!lifting.current.begun) {
            lifting.current.begun = true;
            onBeginCarry?.(lifting.current.square, { x: event.clientX, y: event.clientY });
          }
          return;
        }
        // Yaw only: pitch is a preference now (`CameraPrefs.tilt`), because a
        // two-axis orbit left the board at an angle nobody chose after every
        // wobbly click.
        if (turning.current) renderer.turnCamera(dx);
        else if (panning.current) renderer.panCamera(dx, dy);
      }}
      onPointerUp={(event) => {
        pointers.current.delete(event.pointerId);
        if (!renderer) return;

        // A pinch ends when either finger lifts, and the one still down is the
        // other half of it rather than the start of a pan.
        if (multi.current) {
          pinch.current = null;
          if (pointers.current.size === 0) multi.current = false;
          return;
        }

        const current = drag.current;
        drag.current = null;
        renderer.endCameraDrag();
        if (!current) return;

        panning.current = false;
        turning.current = false;
        // The drop itself is `useCarryGesture`'s; a carry that travelled is
        // stopped short of the click flow by the distance gate below.
        lifting.current = null;
        if (erasing.current) {
          erasing.current = false;
          return;
        }
        if (drawing.current) {
          drawing.current = false;
          const drew = current.distance > DRAG_THRESHOLD_PX;
          onCommitPath();
          // A press that never travelled is a click, and the click flow
          // already knows what an empty square means — so fall through to it
          // rather than swallowing the click as a one-square draw.
          if (drew) return;
        }

        if (current.distance > DRAG_THRESHOLD_PX) return;
        // **Only the left button resolves a click.** The distance gate catches
        // every middle or right *drag*; a stationary middle click would fall
        // through it and select whatever it landed on, which is a piece picked
        // by a gesture that has nothing to do with pieces.
        if (current.button !== 0) return;

        // Resolve the click on release, not on press: until the pointer lifts
        // we do not know whether this was a click or the start of a drag.
        //
        // Arrows before squares: the picker's arrows overhang neighbouring
        // tiles, so a click on one also hits a square underneath. Taking the
        // square would quietly do something other than what was aimed at.
        // The picker's centre target returns DIRECTION_REMOVE rather than a
        // direction; it rides the same path because it is the same click on
        // the same control.
        const direction = renderer.pickDirection(event.clientX, event.clientY);
        if (direction === DIRECTION_REMOVE) {
          onRemovePicked();
          return;
        }
        if (direction >= 0) {
          onPickDirection(direction as Direction, event.shiftKey);
          return;
        }

        const square = renderer.pick(
          event.clientX,
          event.clientY,
          aboutTheFloor(tool),
        );
        if (square >= 0) onPickSquare(square);
        else onPickNothing();
      }}
      // Or the browser's own menu eats the drag the moment it starts.
      onContextMenu={(event) => event.preventDefault()}
      onPointerEnter={() => onHoverChange?.(true)}
      onPointerLeave={() => {
        onHoverChange?.(false);
        // Or a preview stays lit on the last square the pointer was over,
        // with the pointer somewhere else entirely.
        if (hovered.current !== -1) {
          hovered.current = -1;
          onHoverSquare?.(-1);
        }
      }}
      onPointerCancel={(event) => {
        pointers.current.delete(event.pointerId);
        if (pointers.current.size === 0) {
          multi.current = false;
          pinch.current = null;
        }
        // A cancelled pointer never reaches `onPointerUp`, so this is the only
        // place a half-drawn lane gets cleared. Settled rather than abandoned: a cancel is the platform
        // taking the pointer away mid-gesture, which is a lift as far as the
        // camera is concerned.
        if (renderer) endGesture(renderer, true);
      }}
      onDoubleClick={(event) => {
        // The browser's own dblclick, so the timing threshold is the platform's
        // rather than one invented here. The two single clicks underneath it
        // still land; a double click just resolves the piece's one action on
        // top of whatever they did.
        if (!renderer) return;
        const square = renderer.pick(event.clientX, event.clientY);
        if (square >= 0) onDoubleClickSquare(square);
      }}
    />
  );
}
